import { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

export const Contact = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { t } = useLanguage();

  const contactInfo = [
    { icon: Phone, title: t.contact.phone, content: '+49 151 26777707', href: 'tel:+4915126777707', description: t.contact.phoneAvailable },
    { icon: Mail, title: t.contact.email, content: 'service@parkingdus.com', href: 'mailto:service@parkingdus.com', description: t.contact.emailResponse },
    { icon: MapPin, title: t.contact.address, content: 'Kieshecker Weg 141, 40474 Düsseldorf', href: 'https://maps.google.com/?q=Kieshecker+Weg+141+40474+Düsseldorf', description: t.contact.addressNote },
    { icon: Clock, title: t.contact.hours, content: t.contact.hoursValue, href: null, description: t.contact.hoursNote },
  ];

  return (
    <div className="overflow-hidden pt-20" data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <motion.div {...fadeUp} viewport={{ once: true }} className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#D4AF37] text-sm font-medium uppercase tracking-widest mb-4 block">
              {t.contact.subtitle}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              {t.contact.title1}{' '}
              <span className="text-[#D4AF37]">{t.contact.title2}</span>
            </h1>
            <p className="text-white/60 text-base md:text-lg leading-relaxed">
              {t.contact.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="pb-16">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-6 hover:border-[#D4AF37]/30 transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                {item.href ? (
                  <a
                    href={item.href}
                    target={item.href.startsWith('http') ? '_blank' : undefined}
                    rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className="text-[#D4AF37] text-sm hover:underline block mb-1"
                  >
                    {item.content}
                  </a>
                ) : (
                  <p className="text-[#D4AF37] text-sm mb-1">{item.content}</p>
                )}
                <p className="text-white/50 text-xs">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Form */}
            <motion.div {...fadeUp} viewport={{ once: true }}>
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-6">
                {t.contact.formTitle}
              </h2>
              <p className="text-white/60 text-sm leading-relaxed mb-8">
                {t.contact.formDesc}
              </p>

              {isSubmitted ? (
                <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-2xl p-8 text-center">
                  <CheckCircle2 className="w-16 h-16 text-[#D4AF37] mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">{t.contact.successTitle}</h3>
                  <p className="text-white/60 text-sm">{t.contact.successDesc}</p>
                </div>
              ) : (
                <form
                  action="https://api.web3forms.com/submit"
                  method="POST"
                  className="space-y-6"
                  data-testid="contact-form"
                >
                  <input type="hidden" name="access_key" value="eb4fea9b-0b8d-4ce5-a335-270071ea3ce3" />
                  <input type="hidden" name="subject" value="Neue Kontaktanfrage - ParkingDUS" />
                  <input type="hidden" name="from_name" value="ParkingDUS Website" />
                  <input type="checkbox" name="botcheck" className="hidden" />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-white text-sm font-medium mb-2">
                        {t.contact.name} *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full bg-black/20 border border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] rounded-lg px-4 py-3 transition-all"
                        placeholder={t.contact.namePlaceholder}
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-white text-sm font-medium mb-2">
                        {t.contact.emailLabel} *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full bg-black/20 border border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] rounded-lg px-4 py-3 transition-all"
                        placeholder={t.contact.emailPlaceholder}
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-white text-sm font-medium mb-2">
                      {t.contact.phone}
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      className="w-full bg-black/20 border border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] rounded-lg px-4 py-3 transition-all"
                      placeholder={t.contact.phonePlaceholder}
                      data-testid="contact-phone"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-white text-sm font-medium mb-2">
                      {t.contact.subject} *
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      required
                      className="w-full bg-black/20 border border-white/10 text-white focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] rounded-lg px-4 py-3 transition-all appearance-none cursor-pointer"
                      defaultValue=""
                      data-testid="contact-subject"
                    >
                      <option value="" disabled className="bg-[#0A0A0A]">{t.contact.subjectPlaceholder}</option>
                      <option value="Buchungsanfrage" className="bg-[#0A0A0A]">{t.contact.subjects.booking}</option>
                      <option value="Preisanfrage" className="bg-[#0A0A0A]">{t.contact.subjects.price}</option>
                      <option value="Stornierung" className="bg-[#0A0A0A]">{t.contact.subjects.cancel}</option>
                      <option value="Änderung" className="bg-[#0A0A0A]">{t.contact.subjects.change}</option>
                      <option value="Feedback" className="bg-[#0A0A0A]">{t.contact.subjects.feedback}</option>
                      <option value="Sonstiges" className="bg-[#0A0A0A]">{t.contact.subjects.other}</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-white text-sm font-medium mb-2">
                      {t.contact.message} *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      className="w-full bg-black/20 border border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] rounded-lg px-4 py-3 transition-all resize-none"
                      placeholder={t.contact.messagePlaceholder}
                      data-testid="contact-message"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black px-8 py-4 rounded-full font-semibold hover:bg-[#E5C34A] transition-all duration-300 gold-glow"
                    data-testid="contact-submit"
                  >
                    <Send className="w-5 h-5" />
                    {t.contact.submit}
                  </button>
                </form>
              )}
            </motion.div>

            {/* Map */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-6">
                {t.contact.mapTitle}
              </h2>
              <p className="text-white/60 text-sm leading-relaxed mb-8">
                {t.contact.mapDesc}
              </p>
              <div className="aspect-[4/3] rounded-2xl overflow-hidden border border-white/10">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2496.6!2d6.76!3d51.28!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47b8c9!2sDüsseldorf%20Airport!5e0!3m2!1sde!2sde!4v1600000000000!5m2!1sde!2sde"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="ParkingDUS Location"
                  className="grayscale"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <motion.div {...fadeUp} viewport={{ once: true }} className="text-center">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">
              {t.contact.ctaTitle}
            </h2>
            <p className="text-white/60 text-base md:text-lg max-w-2xl mx-auto mb-8">
              {t.contact.ctaDesc}
            </p>
            <a
              href="https://mcdgroup.de/buchungssystem/index.php"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black px-10 py-5 rounded-full font-semibold text-lg hover:bg-[#E5C34A] transition-all duration-300 gold-glow"
              data-testid="contact-book-btn"
            >
              {t.contact.ctaBtn}
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
